import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { useAvailableInvestors, useSelectInvestor } from '../hooks';
import { InvestorContextOption } from '../services/auth-service';

const WaitUntilRedirect = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      <p className="mt-4 text-muted-foreground">Redirecting...</p>
    </div>
  </div>
);

const NoInvestors = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            No Investors Found
          </h2>
          <p className="text-muted-foreground mb-6">
            You don&apos;t have access to any investors. Please contact support.
          </p>
          <button
            onClick={() => navigate('/portal/login')}
            className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

const FailedInvestors = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive mb-4">Error</h2>
          <p className="text-muted-foreground mb-6">
            Failed to load investors. Please try logging in again.
          </p>
          <button
            onClick={() => navigate('/portal/login')}
            className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

const LoadingInvestors = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      <p className="mt-4 text-muted-foreground">Loading investors...</p>
    </div>
  </div>
);

export const InvestorSelector = () => {
  const navigate = useNavigate();
  const {
    data: available,
    isLoading: isLoadingInvestors,
    error: investorsError,
  } = useAvailableInvestors();

  const investors = available?.investors;

  // Pass navigation callback directly to the hook to avoid React Query callback issues
  const handleSelectSuccess = () => {
    navigate('/portal');
  };

  const selectInvestorMutation = useSelectInvestor(handleSelectSuccess);
  const [selectedInvestorId, setSelectedInvestorId] = useState<string | null>(
    null
  );

  const handleSelectInvestor = (investorId: string) => {
    setSelectedInvestorId(investorId);

    selectInvestorMutation.mutate(
      { investor_id: investorId },
      {
        // Toast + logging are handled by useSelectInvestor itself.
        onError: () => setSelectedInvestorId(null),
      }
    );
  };

  if (isLoadingInvestors) {
    return <LoadingInvestors />;
  }

  if (investorsError) {
    return <FailedInvestors />;
  }

  if (!investors || investors.length === 0) {
    // Carry-only: nothing to select, the portal opens on the carry side
    if (available?.has_fund_carry) {
      return <Navigate to="/portal" replace />;
    }
    return <NoInvestors />;
  }

  const [singleInvestor] = investors.length === 1 ? investors : [];

  if (singleInvestor) {
    if (selectInvestorMutation.isError) {
      return <FailedInvestors />;
    }
    if (selectInvestorMutation.isIdle) {
      handleSelectInvestor(singleInvestor.investor_id);
    }
    return <WaitUntilRedirect />;
  }

  const unselectedCardClass = selectInvestorMutation.isPending
    ? 'border-border bg-muted cursor-not-allowed opacity-50'
    : 'border-border hover:border-primary/50 hover:bg-accent';

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Select Investor
          </h1>
          <p className="text-muted-foreground">
            Choose the investor account you&apos;d like to access
          </p>
        </div>

        <div className="space-y-3">
          {investors.map((investor: InvestorContextOption) => (
            <button
              key={investor.investor_id}
              data-testid="investor-select-card"
              onClick={() => handleSelectInvestor(investor.investor_id)}
              disabled={selectInvestorMutation.isPending}
              className={`w-full p-4 rounded-xl border transition-all text-left ${
                selectedInvestorId === investor.investor_id
                  ? 'border-primary bg-primary/10 shadow-md'
                  : unselectedCardClass
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-foreground">
                    {investor.investor_name}
                  </h3>
                  <p className="text-sm text-muted-foreground capitalize">
                    {investor.investor_type}
                  </p>
                </div>
                {selectedInvestorId === investor.investor_id &&
                selectInvestorMutation.isPending ? (
                  <div className="shrink-0">
                    <svg
                      className="animate-spin h-6 w-6 text-primary"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                ) : (
                  <ChevronRight
                    className={`h-5 w-5 transition-colors ${
                      selectedInvestorId === investor.investor_id
                        ? 'text-primary'
                        : 'text-muted-foreground'
                    }`}
                  />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
